import { useLayoutEffect, useState, type ReactNode } from 'react';
import type { WaitForTriggerFunction } from '../hooks/use-wait-for-trigger';
import { ManualMessageViewedEvent } from '../message-viewed-component';

type ValidatedState = 'pending' | 'allowed' | 'blocked';

/** Hide content until validated; avoids flash without off-screen positioning or scrollbar issues. */
const HIDDEN_CONTAINER_STYLE: React.CSSProperties = {
	display: 'none',
};

/**
 * Validates that waitForTrigger was called for triggered messages.
 *
 * - First render: render children inside a hidden container so the template runs and can call
 *   waitForTrigger without any visible flash (blocking during render would create a catch-22).
 * - After commit (useLayoutEffect): if the template did not call waitForTrigger, set state to
 *   'blocked' and return null. If it did, set 'allowed' and make the container visible.
 */
export const TriggeredMessageValidator = ({
	children,
	waitForTrigger,
}: {
	children: ReactNode;
	waitForTrigger?: WaitForTriggerFunction;
}) => {
	const [validated, setValidated] = useState<ValidatedState>('pending');

	useLayoutEffect(() => {
		const calledRef = waitForTrigger?.__calledRef;

		if (calledRef && !calledRef.current) {
			if (process.env.NODE_ENV !== 'production') {
				// eslint-disable-next-line no-console
				console.error(
					'[Post Office] Triggered message rendered without calling waitForTrigger. ' +
						'This message is client-triggered but the component did not call waitForTrigger. ' +
						'Please wrap your content: return waitForTrigger(condition, <YourContent />, onShow);',
				);
			} else {
				// eslint-disable-next-line no-console -- Temporary debug logging for trigger choreography
				console.info('[Post Office] Triggered message rendered without calling waitForTrigger.');
			}
			setValidated('blocked');
		} else {
			// Ref was set (template called waitForTrigger)
			setValidated('allowed');
		}
	}, [waitForTrigger]);

	// eslint-disable-next-line no-console -- Temporary debug logging for trigger choreography
	console.info('[Post Office] Triggered message status', validated);

	if (validated === 'blocked') {
		return null;
	}

	const isHidden = validated === 'pending';
	return (
		<ManualMessageViewedEvent>
			<span
				style={isHidden ? HIDDEN_CONTAINER_STYLE : undefined}
				aria-hidden={isHidden}
				data-testid={isHidden ? 'triggered-message-validator-hidden' : undefined}
			>
				{children}
			</span>
		</ManualMessageViewedEvent>
	);
};
